/**
 * What a meeting looks like once you are in it.
 *
 * Two layouts, one rule. With nobody sharing, everyone gets the same card in
 * one regular grid — with a camera or without, so nobody becomes a floating
 * rectangle in a corner. With a screen going out, the surface takes the stage
 * and the faces become a rail beside it: the screen is the content, the faces
 * are the context. See _design/sketches/Meetings.md §4.
 */

import { useEffect, useRef } from 'react'
import type { ReactElement, ReactNode } from 'react'

import type { Call, CallShareSource, UUID } from '@/api/types'
import { describePeer, type PeerState } from '@/hooks/callPeers'
import type { CallSessionState } from '@/hooks/useCallSession'
import { Avatar } from '@/components/ui'
import styles from './CallWindow.module.css'

/**
 * `describePeer` answers in tokens, not in words. The words live here, once,
 * so the tile and the people panel cannot drift into saying different things
 * about the same connection.
 */
export function peerStateLabel(state: PeerState | null | undefined): string | null {
  switch (state) {
    case 'connecting':
      return 'Connecting…'
    case 'reconnecting':
      return 'Reconnecting…'
    case 'failed':
      return 'Could not connect'
    default:
      return null
  }
}

export function participantName(call: Call, userId: UUID): string {
  const person = call.participants.find((participant) => participant.user?.id === userId)?.user
  if (person === undefined || person === null) return 'Someone'
  const display = person.display_name.trim()
  return display === '' ? person.username : display
}

function MicOffIcon(): ReactElement {
  return (
    <svg width="12" height="12" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
      <rect x="7.6" y="2.8" width="4.8" height="9" rx="2.4" />
      <path d="M4.8 9.4a5.2 5.2 0 0 0 10.4 0M10 14.6V17" />
      <path d="M3.6 3.4 16.4 16.6" />
    </svg>
  )
}

function ScreenIcon(): ReactElement {
  return (
    <svg width="11" height="11" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2.6" y="4.4" width="14.8" height="9.8" rx="2" />
      <path d="M7.4 17h5.2" />
    </svg>
  )
}

/** A media element that follows whatever stream it is handed. */
function Video({
  stream,
  className,
  mirrored = false,
  muted,
}: {
  stream: MediaStream | null
  className?: string
  mirrored?: boolean
  muted: boolean
}): ReactElement {
  const ref = useRef<HTMLVideoElement | null>(null)

  useEffect(() => {
    if (ref.current === null) return
    ref.current.srcObject = stream
  }, [stream])

  return <video ref={ref} className={className} data-self={mirrored} autoPlay playsInline muted={muted} />
}

export interface TileProps {
  name: string
  stream: MediaStream | null
  /** Your own tile: mirrored, and never played back into the room. */
  self?: boolean
  muted?: boolean
  speaking?: boolean
  sharing?: boolean
  state?: PeerState | null
  userId?: UUID
  compact?: boolean
}

/**
 * One person. Everyone gets this card, camera or no camera; state is said on
 * the card it belongs to rather than as a banner over the whole meeting.
 */
export function CallTile({
  name,
  stream,
  self = false,
  muted = false,
  speaking = false,
  sharing = false,
  state = null,
  userId,
  compact = false,
}: TileProps): ReactElement {
  const failed = state === 'failed'
  const label = peerStateLabel(state)

  return (
    <div
      className={styles.tileWrap}
      data-speaking={speaking}
      data-state={failed ? 'failed' : state === 'reconnecting' ? 'reconnecting' : undefined}
    >
      {stream === null ? (
        <div className={styles.tileFallback}>
          <Avatar name={name} id={userId} size={compact ? 'md' : 'lg'} decorative />
        </div>
      ) : (
        <Video stream={stream} className={styles.tile} mirrored={self} muted={self} />
      )}

      {sharing ? (
        <span className={styles.sharingBadge}>
          <ScreenIcon />
          Sharing
        </span>
      ) : label === null ? null : (
        <span className={styles.tileState} data-state={failed ? 'failed' : undefined}>
          {label}
        </span>
      )}

      <span className={styles.tileLabel}>
        {muted ? (
          <span className={styles.mutedBadge} aria-label="Microphone off">
            <MicOffIcon />
          </span>
        ) : null}
        <span className={styles.tileName}>{name}</span>
      </span>
    </div>
  )
}

export interface StageTile {
  id: string
  name: string
  stream: MediaStream | null
  self: boolean
  muted: boolean
  speaking: boolean
  sharing: boolean
  state: PeerState | null
  userId?: UUID
}

/**
 * Everyone in the call, in a stable order: the people the server lists, then
 * you. A tile never moves because someone spoke — a grid that reorders itself
 * cannot be followed.
 */
export function stageTiles(
  call: Call,
  session: CallSessionState,
  selfUserId: UUID | undefined,
): StageTile[] {
  const sharingUserId = session.share.state?.userId ?? null

  const remote: StageTile[] = Object.keys(session.peerStates).map((userId) => ({
    id: userId,
    userId,
    name: participantName(call, userId),
    stream: session.remoteTracks.find((track) => track.userId === userId)?.stream ?? null,
    self: false,
    muted: session.remoteMedia[userId]?.audioMuted === true,
    speaking: session.speaking[userId] === true,
    sharing: sharingUserId === userId,
    state: describePeer(session.peerStates[userId]),
  }))

  if (session.localStream !== null) {
    remote.push({
      id: 'self',
      userId: selfUserId,
      name: 'You',
      stream: session.cameraEnabled ? session.localStream : null,
      self: true,
      muted: !session.micEnabled,
      speaking: false,
      sharing: sharingUserId !== null && sharingUserId === selfUserId,
      state: null,
    })
  }

  return remote
}

export function CallGrid({ tiles }: { tiles: StageTile[] }): ReactElement {
  return (
    <div className={styles.grid} data-count={tiles.length}>
      {tiles.map((tile) => (
        <CallTile key={tile.id} {...tile} />
      ))}
    </div>
  )
}

/**
 * The presentation layout. Four faces beside the surface and a count for the
 * rest: a rail that scrolls past four is a rail nobody scrolls.
 */
/** What the browser said was picked, in the words people use for it. */
export function surfaceNoun(source: CallShareSource | null | undefined): string {
  switch (source) {
    case 'window':
      return 'window'
    case 'tab':
      return 'tab'
    default:
      return 'screen'
  }
}

export function CallPresentation({
  surface,
  presenter,
  source,
  own,
  tiles,
  rail = true,
  children,
}: {
  surface: MediaStream | null
  presenter: string
  source?: CallShareSource | null
  /** We are the one sharing: the surface is shown small, never full size. */
  own: boolean
  tiles: StageTile[]
  /** Off while the people panel is open: the two say the same thing, and
   *  both at once leave nothing of the screen. */
  rail?: boolean
  children?: ReactNode
}): ReactElement {
  const shown = tiles.slice(0, 4)
  const hidden = tiles.length - shown.length

  return (
    <div className={styles.presentation}>
      <div className={styles.surface}>
        <Video stream={surface} className={styles.surfaceVideo} muted />
        <span className={styles.surfaceLabel}>
          <ScreenIcon />
          {own ? `Your ${surfaceNoun(source)}` : `${presenter}’s ${surfaceNoun(source)}`}
        </span>
        {children === undefined ? null : <div className={styles.surfaceActions}>{children}</div>}
      </div>

      {rail ? (
        <div className={styles.rail}>
          {shown.map((tile) => (
            <CallTile key={tile.id} {...tile} compact />
          ))}
          {hidden > 0 ? <div className={styles.railMore}>{hidden} more</div> : null}
        </div>
      ) : null}
    </div>
  )
}

/** The people panel: the same names as the member list, with what they are doing. */
export function CallPeople({
  tiles,
  total,
  onClose,
}: {
  tiles: StageTile[]
  total: number
  onClose: () => void
}): ReactElement {
  return (
    <div className={styles.people} aria-label="People in the call">
      <div className={styles.peopleHead}>
        <span className={styles.peopleTitle}>People</span>
        <span className={styles.peopleCount}>{total} of 8</span>
        <button type="button" className={styles.pillButton} aria-label="Close the people panel" onClick={onClose}>
          <svg width="13" height="13" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <path d="M5.4 5.4 14.6 14.6M14.6 5.4 5.4 14.6" />
          </svg>
        </button>
      </div>

      {tiles.map((tile) => (
        <div key={tile.id} className={styles.personRow}>
          <Avatar name={tile.name} id={tile.userId} size="sm" decorative />
          <span className={styles.personName}>{tile.name}</span>
          {tile.sharing ? (
            <span className={styles.personState} data-speaking="true">
              Sharing
            </span>
          ) : tile.speaking ? (
            <span className={styles.personState} data-speaking="true">
              Speaking
            </span>
          ) : peerStateLabel(tile.state) !== null ? (
            <span className={styles.personState}>{peerStateLabel(tile.state)}</span>
          ) : null}
          {tile.muted ? (
            <span className={styles.mutedBadge} aria-label={`${tile.name}, microphone off`}>
              <MicOffIcon />
            </span>
          ) : null}
        </div>
      ))}
    </div>
  )
}
